import {
  SOCIAL_FORMAT_LABELS,
  SOCIAL_PIECE_STATUS_LABELS,
  socialCalendarItemSchema,
  type SocialCalendarItem,
} from '@growup/site-shared';
import { Button, Card, cx, Notice, StatusBadge } from '@growup/ui';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf } from '../api';
import { PIECE_TONE } from './PlansView';

const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MONTH = new Intl.DateTimeFormat('pt-PT', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** O mes de `month` (`aaaa-mm`): o primeiro e o ultimo dia. */
export function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
}

export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return iso(new Date(Date.UTC(y, m - 1 + by, 1))).slice(0, 7);
}

/** As semanas do mes, de segunda a domingo; `null` nos dias de outros meses. */
export function monthWeeks(month: string): (string | null)[][] {
  const { from, to } = monthRange(month);
  const first = new Date(`${from}T00:00:00Z`);
  const lead = (first.getUTCDay() + 6) % 7;
  const days: (string | null)[] = Array<string | null>(lead).fill(null);
  for (let d = first; iso(d) <= to; d = new Date(d.getTime() + 86_400_000)) days.push(iso(d));
  while (days.length % 7 !== 0) days.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

const today = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Lisbon' }).format(new Date());

/**
 * Calendario editorial (RF-RS10): as pecas de todos os meus planos pela data
 * prevista de publicacao. Carregar numa peca abre o plano.
 */
export function CalendarView({ onOpenPlan }: { onOpenPlan: (planId: string) => void }) {
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [items, setItems] = useState<SocialCalendarItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const { from, to } = monthRange(month);
    setItems(null);
    setError(null);
    getJson(`/api/site/social/calendar?from=${from}&to=${to}`, z.array(socialCalendarItemSchema))
      .then(setItems)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [month]);

  const onDay = (day: string) => items?.filter((i) => i.publishOn === day) ?? [];
  const now = today();
  const label = MONTH.format(new Date(`${month}-01T00:00:00Z`));

  const item = (i: SocialCalendarItem) => (
    <button
      key={i.pieceId}
      type="button"
      onClick={() => onOpenPlan(i.planId)}
      className="flex w-full flex-col gap-0.5 border border-gu-line bg-gu-raised p-1 text-left text-caption hover:border-gu-accent"
    >
      <span className="font-semibold">{i.title}</span>
      <span className="text-gu-ink-muted">
        {i.planName} · {SOCIAL_FORMAT_LABELS[i.format]}
      </span>
      <StatusBadge tone={PIECE_TONE[i.status]}>{SOCIAL_PIECE_STATUS_LABELS[i.status]}</StatusBadge>
    </button>
  );

  return (
    <Card
      headingLevel={2}
      title="Calendário"
      actions={
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label="Mês anterior"
            onClick={() => setMonth(shiftMonth(month, -1))}
          >
            ←
          </Button>
          <span className="min-w-36 text-center font-sub font-bold uppercase">{label}</span>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Mês seguinte"
            onClick={() => setMonth(shiftMonth(month, 1))}
          >
            →
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-gu-ink-muted">
          As peças dos planos pela data prevista de publicação. As peças sem data não aparecem.
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {items === null && !error ? <p>A carregar…</p> : null}
        {items?.length === 0 ? <p>Nenhuma peça para publicar neste mês.</p> : null}

        {/* Computador: a grelha do mes. */}
        <table className="hidden w-full table-fixed border-collapse md:table">
          <thead>
            <tr>
              {WEEKDAYS.map((d) => (
                <th
                  key={d}
                  className="p-1 text-left font-sub text-label uppercase text-gu-ink-muted"
                >
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthWeeks(month).map((week, w) => (
              <tr key={w}>
                {week.map((day, d) => (
                  <td
                    key={day ?? `v${String(w)}-${String(d)}`}
                    className={cx(
                      'h-24 border border-gu-line p-1 align-top',
                      day === null && 'bg-gu-sunken',
                    )}
                  >
                    {day ? (
                      <div className="flex flex-col gap-1">
                        <span
                          className={cx('text-caption', day === now && 'font-bold text-gu-accent')}
                        >
                          {Number(day.slice(8))}
                        </span>
                        {onDay(day).map(item)}
                      </div>
                    ) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Telemovel: so os dias com pecas. */}
        <ol className="flex flex-col gap-3 md:hidden">
          {[...new Set(items?.map((i) => i.publishOn))].map((day) => (
            <li key={day} className="flex flex-col gap-1">
              <h3 className="font-sub text-label font-bold uppercase">
                {day.split('-').reverse().join('/')}
              </h3>
              {onDay(day).map(item)}
            </li>
          ))}
        </ol>
      </div>
    </Card>
  );
}
